import _ from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {IconName} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {CrmSalesforceFieldModel} from '../../../../../models/api/crmSalesforceFieldModel';
import {CrmSalesforceMetadataModel} from '../../../../../models/api/crmSalesforceMetadataModel';
import {DropdownItemIcon} from '../../../../common/dropdowns/dropdownItemIcon';
import {DropdownItemMultiline} from '../../../../common/dropdowns/dropdownItemMultiline';
import {DeprecatedSearchDropdown} from '../../../../common/dropdowns/search/deprecatedSearchDropdown';
import {useSyncDropdownSource} from '../../../../common/dropdowns/source/hooks/useSyncDropdownSource';

/*
 * Constants.
 */

const dropdownItemHeight = 48;
const dropdownHeight = 342;

/*
 * Props.
 */

interface SalesforceCustomFieldsDropdownProps {
  alreadySelectedIds: ReadonlyArray<string>;
  popoverProps: PopoverTransferProps;
  selectedCustomFieldId?: string;
  salesforceFields?: ReadonlyArray<CrmSalesforceFieldModel>;
  salesforceMetadata?: CrmSalesforceMetadataModel;
  computeSalesforceCustomFieldIcon: (
    customFieldId: string,
    salesforceFields?: ReadonlyArray<CrmSalesforceFieldModel>,
    salesforceMetadata?: CrmSalesforceMetadataModel,
  ) => IconName | undefined;
  onSelect: HandlerOf<CrmSalesforceFieldModel>;
}

/*
 * Style.
 */

const StyledTypeSpan = styled.span`
  text-transform: capitalize;
`;

/*
 * Component.
 */

export const SalesforceCustomFieldsDropdown: FC<SalesforceCustomFieldsDropdownProps> = (props) => {
  const {
    alreadySelectedIds,
    popoverProps,
    selectedCustomFieldId,
    salesforceFields,
    salesforceMetadata,
    onSelect,
    computeSalesforceCustomFieldIcon,
  } = props;

  const dropdownSource = useSyncDropdownSource<CrmSalesforceFieldModel>(
    {
      items: _(salesforceFields ?? [])
        // Make sure we filter out any already selected ids.
        .filter((field) => selectedCustomFieldId === field.id || !alreadySelectedIds.includes(field.id))
        // Move the selected item to the top of the list.
        .sort((field) => (field.id === selectedCustomFieldId ? -1 : 1))
        .value(),
      renderRow: (item: CrmSalesforceFieldModel) => {
        const isChecked = selectedCustomFieldId === item.id;
        const iconName =
          computeSalesforceCustomFieldIcon(item.id, salesforceFields, salesforceMetadata) ?? 'contentSmall';
        return (
          <DropdownItem onSelect={isChecked ? undefined : () => onSelect(item)} isChecked={isChecked}>
            <DropdownItemIcon iconName={iconName} iconSize={16} label={null} />
            <DropdownItemMultiline
              title={item.label}
              subtitle={<StyledTypeSpan>{item.type}</StyledTypeSpan>}
            />
          </DropdownItem>
        );
      },
      rowHeightPredicate: () => dropdownItemHeight,
      rowFilter: (items, query) => {
        // If we do not have a query, return all the items.
        if (query.trim() === '') {
          return items;
        }
        return items.filter((item) => item.label.toLowerCase().includes(query.toLowerCase()));
      },
    },
    [],
  );

  return (
    <DeprecatedSearchDropdown
      {...popoverProps}
      title={renderDropdownTitle()}
      source={dropdownSource}
      height={dropdownHeight}
      placement="top-start"
    />
  );
};

/*
 * Helpers.
 */

function renderDropdownTitle() {
  return <Trans id="accounts-crm-salesforce-custom-fields-dropdown-title">Salesforce account fields</Trans>;
}
